import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// 20s default ceiling — fail fast on dropped connections (was 30s, which left
// users staring at a spinner). Long-running PDF endpoints opt-in via { timeout: 60000 }.
const DEFAULT_TIMEOUT = 20000;

const api = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ndis_hub_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Multi-client: workers attach the active client id so server can scope their request.
  const activeClient = localStorage.getItem("ndis_active_client_id");
  if (activeClient) config.headers["X-Active-Client-Id"] = activeClient;
  // PDFs and other binary blobs can take longer (ReportLab + email) — bump the
  // ceiling automatically so we don't kill them at the default 20s.
  if (config.responseType === "blob") {
    config.timeout = Math.max(config.timeout || 0, 60000);
  }
  return config;
});

// ---- Transient-failure auto-retry ----
// Goal: hide the most common cause of red "Network Error" toasts on flaky mobile
// connections — a single dropped packet between the client and the edge. Reads are
// retried aggressively; writes are retried ONLY on "request never reached server"
// errors (ERR_NETWORK) to avoid the risk of duplicate creates.
const READ_RETRY_LIMIT = 3;
const WRITE_RETRY_LIMIT = 1;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const config = err?.config;
    if (!config) return Promise.reject(err);
    const method = (config.method || "get").toLowerCase();
    const isRead = method === "get" || method === "head";
    const status = err?.response?.status;

    // "Never reached server" — safe to retry for any method.
    const neverReached = err.code === "ERR_NETWORK" || err.message === "Network Error";
    // "Reached server-ish" — timeout or 5xx. Safe for reads only.
    const partialFailure = err.code === "ECONNABORTED" || (status >= 500 && status <= 599);

    const transient = neverReached || (isRead && partialFailure);
    const limit = isRead ? READ_RETRY_LIMIT : WRITE_RETRY_LIMIT;
    config.__retryCount = config.__retryCount || 0;
    if (!transient || config.__retryCount >= limit) return Promise.reject(err);

    config.__retryCount += 1;
    // 400ms, 800ms, 1200ms — exponential-ish backoff with a tiny jitter.
    const backoff = 400 * config.__retryCount + Math.random() * 200;
    await new Promise((r) => setTimeout(r, backoff));
    return api(config);
  }
);

// ---- Stale-while-revalidate cache for list GETs ----
// In-memory only (intentional — token-scoped data shouldn't outlive the tab session).
// Pages call `cachedGet(url, params, onData)` and render the cached value instantly
// while a fresh fetch runs in the background.
const _cache = new Map();

function cacheKey(url, params) {
  return url + "|" + JSON.stringify(params || {});
}

export const apiCache = {
  read(url, params) {
    return _cache.get(cacheKey(url, params));
  },
  write(url, params, data) {
    _cache.set(cacheKey(url, params), data);
  },
  // Drop every cached entry whose URL starts with `prefix`. Call after a mutation
  // so the next navigation refetches fresh data (e.g. after deleting a timesheet,
  // invalidate "/timesheets" and "/notifications").
  invalidate(prefix) {
    for (const k of _cache.keys()) {
      if (k.startsWith(prefix + "|") || k.startsWith(prefix + "/")) _cache.delete(k);
    }
  },
  clear() {
    _cache.clear();
  },
};

/**
 * Fetch a list with stale-while-revalidate semantics.
 *
 * - Synchronously fires `onData(cachedValue)` if we have one (instant paint).
 * - Asynchronously fetches fresh data; on success fires `onData(freshValue)`
 *   and updates the cache.
 *
 * Error handling:
 *   - If we had a cache hit, background-refresh failures are SWALLOWED — the
 *     user is already looking at usable data and a transient network blip
 *     shouldn't redirect their attention with a scary red toast.
 *   - If the cache was cold (no data on screen), errors throw so the caller
 *     can show a toast.
 */
export async function cachedGet(url, params, onData) {
  const cached = apiCache.read(url, params);
  const hadCache = cached !== undefined;
  if (hadCache) onData(cached);
  try {
    const { data } = await api.get(url, { params });
    apiCache.write(url, params, data);
    onData(data);
    return data;
  } catch (err) {
    if (hadCache) {
      // Background refresh failed but the user already sees cached data.
      // Log to console for diagnostics; don't surface a toast.
      // eslint-disable-next-line no-console
      console.warn(`[cachedGet] background refresh of ${url} failed:`, err?.message || err);
      return cached;
    }
    throw err;
  }
}

// Clear cache on logout so a fresh user doesn't see the previous user's data flash.
export function clearApiCache() {
  apiCache.clear();
}

export function formatApiError(err) {
  // Subscription gate (HTTP 402): give a friendly message instead of raw "subscription_required".
  if (err?.response?.status === 402) {
    return "An active subscription is required to make changes. Open Settings → Subscription to renew.";
  }
  // Friendlier copy for connectivity issues than the raw axios "Network Error".
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    return "Can't reach the server right now. Check your internet and try again in a moment.";
  }
  if (err?.code === "ECONNABORTED") {
    return "That request took too long. Please try again — if it keeps happening, refresh the page.";
  }
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return JSON.stringify(d);
}

export default api;
